import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useState,
    useEffect,
    useRef
} from "react";
import { Feature } from "ol";
import { DrawType } from "../Map/enums";
import { Collection } from "ol";
import { Draw, Modify, Select, DoubleClickZoom } from "ol/interaction";
import { getDrawingSymbol } from "../Map/symbols";
import { config } from "../Map/config";
import { getMap } from "../Forms/DrawingForm";
import { useRegistrationContext } from "./RegistrationContext";

const drawingLayerId = config.default.drawingLayerId;
export interface IDrawingContext {
    drawType: DrawType;
    setDrawType: Dispatch<SetStateAction<DrawType>>;
    onDrawStart: (type: DrawType) => void;
    newFeature: Feature;
    setNewFeature: Dispatch<SetStateAction<Feature>>;
    onNoteChange: () => void;
    onDeleteFeature: (deletable: Feature) => void;
    selectedFeature: Feature;
    setSelectedFeature: Dispatch<SetStateAction<Feature>>;
    setDrawingAllowed: Dispatch<SetStateAction<boolean>>;
}

const DrawingContext = createContext<IDrawingContext>(null);

export const useDrawingContext = (): IDrawingContext => useContext(DrawingContext);

export const DrawingContextProvider: FC = (props: React.PropsWithChildren<IDrawingContext>) => {
    const [drawType, setDrawType] = useState<DrawType>();
    const [newFeature, setNewFeature] = useState<Feature>();
    const [selectedFeature, setSelectedFeature] = useState<Feature>();
    const [drawingAllowed, setDrawingAllowed] = useState<boolean>(false);

    const { drawnFeatures, setDrawnFeatures } = useRegistrationContext();

    const drawRef = useRef<Draw>();
    const selectRef = useRef<Select>();
    const modifyRef = useRef<Modify>();

    const onNoteChange = useCallback(() => {
        setDrawnFeatures(drawnFeatures);
    }, [drawnFeatures]);

    const onDeleteFeature = useCallback(
        (deletable) => {
            const features = drawnFeatures.filter((f) => f.get("TEMPID") !== deletable.get("TEMPID"));
            setDrawnFeatures(features);
            const drawingSource = getMap()
                .getLayers()
                .getArray()
                .find((l) => l.get("id") === drawingLayerId)
                .getSource();
            drawingSource.removeFeature(deletable);
        },
        [drawnFeatures]
    );

    useEffect(() => {
        if (!newFeature) {
            return;
        }
        newFeature.setProperties({ TEMPID: Date.now(), DESCRIPTION: "", DRAWTYPE: drawType });
        setDrawnFeatures([...drawnFeatures, newFeature]);
        setNewFeature(null);
        setDrawType(undefined);
    }, [drawnFeatures, newFeature]);

    useEffect(() => {
        drawnFeatures && drawnFeatures.length && drawnFeatures.map((f, i) => f.set("NR", (i + 1).toString()));
        onNoteChange();
    }, [drawnFeatures]);

    useEffect(() => {
        if (selectedFeature) {
            addSelectInteraction(selectedFeature);
            addModifyInteraction();
            removeDrawInteraction();
            setDrawType(undefined);
        } else {
            removeModifyInteraction();
        }
    }, [selectedFeature]);

    const onModifyFeature = () =>
        setDrawnFeatures(
            getMap()
                .getLayers()
                .getArray()
                .find((l) => l.get("id") === drawingLayerId)
                .getSource()
                .getFeatures()
        );

    const onDrawStart = (type: DrawType) => setDrawType(type);

    useEffect(() => {
        if (drawType && drawingAllowed) {
            drawRef.current && removeDrawInteraction();
            setSelectedFeature(null);
            selectRef.current && removeSelectInteraction();

            addDrawInteraction(drawType);
            setDoubleClickZoom(false);
        } else {
            removeDrawInteraction();
            setSelectedFeature(null);
            setTimeout(() => setDoubleClickZoom(true), 260); // doubleClick lasts 250ms
        }
    }, [drawingAllowed, drawType]);

    const onDrawEnd = () => {
        removeDrawInteraction();
        !selectRef.current && addSelectInteraction();
        setTimeout(() => setDoubleClickZoom(true), 260); // doubleClick lasts 250ms
    };

    const addDrawInteraction = (type) => {
        const source = getMap()
            .getLayers()
            .getArray()
            .find((l) => l.get("id") === drawingLayerId)
            .getSource();

        drawRef.current = new Draw({
            source,
            type: type === DrawType.Point ? "Point" : "LineString",
            stopClick: true
        });
        getMap().addInteraction(drawRef.current);
        drawRef.current.once("drawend", onDrawEnd);
    };

    const removeDrawInteraction = () => {
        const map = getMap();
        map && map.removeInteraction(drawRef.current);
        drawRef.current = null;
    };

    const addSelectInteraction = (selected?: Feature) => {
        removeSelectInteraction();
        selectRef.current = new Select({
            hitTolerance: 10,
            style: (feature) => getDrawingSymbol(feature.get("DRAWTYPE"), feature.get("NR"), true)
        });
        selectRef.current.on("select", (e) =>
            e.selected[0] ? setSelectedFeature(e.selected[0]) : setSelectedFeature(null)
        );
        getMap().addInteraction(selectRef.current);

        if (selected && selectRef.current) {
            selectRef.current.getFeatures().clear();
            selectRef.current.getFeatures().push(selected);
        }
    };

    const removeSelectInteraction = () => {
        getMap().removeInteraction(selectRef.current);
        selectRef.current = null;
    };

    const addModifyInteraction = () => {
        removeModifyInteraction();
        const featureCollection = new Collection();
        featureCollection.push(selectedFeature);
        modifyRef.current = new Modify({
            features: featureCollection,
            pixelTolerance: 15,
            style: (feature) => {
                const style = getDrawingSymbol(
                    feature.get("features")[0].get("DRAWTYPE"),
                    feature.get("features")[0].get("NR"),
                    true,
                    true
                );
                return style;
            }
        });
        modifyRef.current.on("modifyend", () => onModifyFeature());
        getMap().addInteraction(modifyRef.current);
    };

    const removeModifyInteraction = () => {
        const map = getMap();
        map && map.removeInteraction(modifyRef.current);
        modifyRef.current = null;
    };

    const setDoubleClickZoom = (active: boolean) => {
        const interactions = getMap().getInteractions();
        for (let i = 0; i < interactions.getLength(); i++) {
            const interaction = interactions.item(i);
            if (interaction instanceof DoubleClickZoom) {
                interaction.setActive(active);
                break;
            }
        }
    };

    const values = {
        drawType,
        setDrawType,
        onDrawStart,
        newFeature,
        setNewFeature,
        onNoteChange,
        onDeleteFeature,
        selectedFeature,
        setSelectedFeature,
        setDrawingAllowed
    };

    return <DrawingContext.Provider value={values}>{props.children}</DrawingContext.Provider>;
};
